import { z } from 'zod';
import { artifactManifestEntrySchema } from './artifact.ts';
import {
  datasetDeclarationSchema,
  observationDatasetSchema,
} from './dataset.ts';
import { jsonObjectSchema } from './json.ts';
import { visualizationPageSpecSchema } from './page.ts';
import { runtimeProjectSpecSchema } from './runtime.ts';

const staticObservationDatasetSchema = observationDatasetSchema.refine(
  dataset => dataset.updateMode === 'replace',
  {
    path: ['updateMode'],
    message: 'Project 内嵌静态数据必须使用 replace 快照',
  },
);

export const visualizationBriefSchema = z.object({
  purpose: z.string().max(100000).optional(),
  audience: z.string().max(100000).optional(),
  knowledgeAnchor: z.string().max(100000).optional(),
  problemStatement: z.string().max(100000).optional(),
  desiredEvidence: z.array(z.string().min(1).max(100000)).max(1000).default([]),
}).strict();

export const projectMetadataSchema = z.object({
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
  createdBy: z.string().optional(),
  tags: z.array(z.string().min(1).max(1000)).max(1000).default([]),
}).strict();

export const visualizationProjectSpecSchema = z.object({
  schemaVersion: z.literal('1.0.0'),
  id: z.string().min(1).max(200),
  revision: z.number().int().nonnegative().default(0),
  title: z.string().max(100000).default(''),
  brief: visualizationBriefSchema.default({ desiredEvidence: [] }),
  pages: z.array(visualizationPageSpecSchema).max(100).default([]),
  datasets: z.array(datasetDeclarationSchema).max(1000).default([]),
  staticDatasets: z.array(staticObservationDatasetSchema).max(100).default([]),
  runtime: runtimeProjectSpecSchema.default({
    sessions: [],
    seed: 20260726,
    defaultSpeed: 1,
  }),
  artifacts: z.array(artifactManifestEntrySchema).max(5000).default([]),
  pedagogy: jsonObjectSchema.optional(),
  metadata: projectMetadataSchema.default({ tags: [] }),
}).strict();

export type VisualizationBrief = z.infer<typeof visualizationBriefSchema>;
export type ProjectMetadata = z.infer<typeof projectMetadataSchema>;
export type VisualizationProjectSpec = z.infer<
  typeof visualizationProjectSpecSchema
>;

export function createEmptyVisualizationProject(
  id = 'visualization-project',
): VisualizationProjectSpec {
  return visualizationProjectSpecSchema.parse({
    schemaVersion: '1.0.0',
    id,
    revision: 0,
    title: '',
    brief: {},
    pages: [],
    datasets: [],
    staticDatasets: [],
    runtime: {},
    artifacts: [],
    metadata: {},
  });
}
